import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, webPageLd, faqLd, type FaqItem } from '../seo';
import cities from '../data/cities.json';

interface CityLink {
  slug: string;
  name: string;
  note?: string;
}

export interface UseCaseContent {
  slug: string;
  title: string;
  description: string;
  canonicalPath: string;
  ogImage: string;
  heroIntro: string;
  painPoints: string[];
  solutions: { title: string; body: string }[];
  citySpotlights: CityLink[];
  faqs: { question: string; answer: string }[];
  primaryCta: { label: string; to: string; color?: string };
  secondaryCta?: { label: string; to: string };
  breadcrumbs: { label: string; to?: string }[];
  bottomNote?: ReactNode;
}

export default function UseCaseTemplate({
  content,
}: {
  content: UseCaseContent;
}) {
  const canonical = `https://parksy.rent${content.canonicalPath}`;
  const schemas = [
    organizationLd(),
    webSiteLd(),
    webPageLd({ name: content.title, description: content.description, url: canonical }),
    faqLd(content.faqs as FaqItem[]),
  ];

  return (
    <>
      <Breadcrumbs
        items={content.breadcrumbs}
        pageTitle={content.title}
        pageDescription={content.description}
        extraSchemas={schemas}
      />
      <article className="max-w-4xl mx-auto px-4 py-10">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">{content.title}</h1>
          <p className="text-lg text-gray-700 leading-relaxed">{content.heroIntro}</p>
        </header>

        {content.painPoints.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Why this is hard in India</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {content.painPoints.map((p, i) => (
                <li key={i} className="bg-white border rounded-lg p-3 text-sm text-gray-700">
                  {p}
                </li>
              ))}
            </ul>
          </section>
        )}

        {content.solutions.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How Parksy helps</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {content.solutions.map((s, i) => (
                <div key={i} className="bg-white border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                  <p className="text-sm text-gray-700">{s.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Find it in your city</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {content.citySpotlights.map((c) => (
              <Link
                key={c.slug}
                to={`/${c.slug}`}
                className="bg-white border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500 hover:text-parksy-700"
              >
                <span className="font-medium">{c.name}</span>
                {c.note ? <span className="block text-xs text-gray-500 mt-0.5">{c.note}</span> : null}
              </Link>
            ))}
          </div>
          <details className="mt-3">
            <summary className="text-sm text-parksy-700 cursor-pointer hover:underline">
              Show all {cities.length} cities
            </summary>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  className="bg-gray-50 border rounded-lg px-3 py-2 text-gray-700 hover:border-parksy-500"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </details>
        </section>

        <section className="mb-10">
          <div className="flex flex-wrap gap-3">
            <Link
              to={content.primaryCta.to}
              className={`inline-block ${content.primaryCta.color ?? 'bg-parksy-600 hover:bg-parksy-700'} text-white font-medium px-5 py-2.5 rounded-lg`}
            >
              {content.primaryCta.label}
            </Link>
            {content.secondaryCta && (
              <Link
                to={content.secondaryCta.to}
                className="inline-block bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium px-5 py-2.5 rounded-lg"
              >
                {content.secondaryCta.label}
              </Link>
            )}
          </div>
        </section>

        {content.faqs.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">FAQ</h2>
            <div className="space-y-4">
              {content.faqs.map((f, i) => (
                <div key={i} className="bg-white border rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{f.question}</h3>
                  <p className="text-sm text-gray-700">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {content.bottomNote && (
          <footer className="text-xs text-gray-500 border-t pt-4">{content.bottomNote}</footer>
        )}
      </article>
    </>
  );
}
